<template>
  <div v-if="$can('view', 'Yacht')">
    <div class="subtitle-1 pa-4">
      <v-row>
        <v-col>
          Yacht Configuration
        </v-col>
        <v-col cols="auto">
          <v-btn
            text
            color="primary"
            small
            class="text-none"
            @click="$emit('edit:yacht')"
          >
            <v-icon
              left
              x-small
            >
              fa fa-pen
            </v-icon>
            Reconfigure
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-card
      class="pa-0 mb-16"
      elevation="10"
    >
      <div class="v-data-table theme--light">
        <div class="v-data-table__wrapper">
          <table>
            <thead>
              <tr>
                <th class="text-left">
                  Category
                </th>
                <th class="text-left">
                  Label
                </th>
                <th
                  v-if="$can('view-sale-price', 'YachtEquipmentPack') || $can('view-sale-price', 'Yacht')"
                  class="text-left"
                >
                  Price
                </th>
                <th class="text-left">
                  Installed By
                </th>
                <th class="text-left">
                  Supplier
                </th>
                <th v-if="$can('view-purchase-price', 'Yacht') || $can('view-purchase-price', 'YachtEquipmentPack')">
                  Purchase Price
                </th>
                <th v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  Invoiced Amount
                </th>
                <th v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  Invoice
                </th>
                <th v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  Diff
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Base yacht model</strong>
                </td>
                <td>
                  <div class="font-weight-bold">
                    {{ yacht.model.full_label }}
                  </div>
                  <div class="caption grey--text">
                    For year {{ yacht.production_year }}
                  </div>
                </td>
                <td v-if="$can('view-sale-price', 'Yacht')">
                  <strong>{{ yacht.base_sale_price | currency(yacht.currency) }}</strong>
                </td>
                <td />
                <td>
                  {{ yacht.model.manufacturer.name }}
                </td>
                <td
                  v-if="$can('view-purchase-price', 'Yacht')"
                  class="font-weight-bold"
                >
                  {{ yacht.base_purchase_price / (yacht.currency_exchange_rate || 1) | currency(yacht.original_currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <currency-input
                    v-model="yacht.base_invoiced_price"
                    dense
                    hide-details
                    outlined
                    :suffix="yacht.original_currency"
                    style="width: 150px"
                    @change="updateYachtInvoicedAmount($event)"
                  />
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <div
                    v-for="document in yacht.invoice_documents"
                    :key="'document_' + yacht.id + document.id"
                  >
                    <a
                      href="#"
                      @click.prevent="openDocument(document.file.id)"
                    >
                      {{ document.title }}
                    </a>
                  </div>
                  <div
                    v-for="file in yachtFiles"
                    :key="'yacht' + file.id"
                    class="d-inline-flex"
                  >
                    <v-progress-circular
                      v-if="file.active"
                      color="grey"
                      :size="18"
                      :width="2"
                      indeterminate
                    />
                    {{ file.name }}
                  </div>
                  <div class="mt-1">
                    <file-upload
                      ref="uploadyacht"
                      key="uploadyacht"
                      v-model="yachtFiles"
                      input-id="yachtfile"
                      :post-action="uploadUrl"
                      multiple
                      :data="{ type: 'invoices', yacht_id: yacht.id }"
                      :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                      style="cursor: pointer"
                      @input-file="inputYachtFile(...arguments)"
                    >
                      <v-btn
                        color="grey darken-2"
                        class="text-none ma-0"
                        small
                        text
                      >
                        <v-icon
                          left
                          small
                        >
                          add
                        </v-icon>
                        Add Invoice
                      </v-btn>
                    </file-upload>
                  </div>
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <span
                    :class="{ 'error--text': invoicedAmounts.yacht.diff > 0 }"
                    class="font-weight-bold"
                  >
                    {{ Math.abs(invoicedAmounts.yacht.diff) | currency(yacht.original_currency ) }}
                  </span>
                </td>
              </tr>
              <tr>
                <td colspan="2">
                  <strong>Equipment</strong>
                </td>
                <td
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.equipment_sale_price | currency(yacht.currency) }}
                </td>
                <td colspan="2" />
                <td
                  v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.equipment_purchase_price | currency(yacht.currency) }}
                </td>
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ invoicedAmounts.equipment.amount | currency(yacht.currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')" />
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <span
                    :class="{ 'error--text': invoicedAmounts.equipment.diff > 0 }"
                    class="font-weight-bold"
                  >
                    {{ Math.abs(invoicedAmounts.equipment.diff) | currency(yacht.currency) }}
                  </span>
                </td>
              </tr>
              <tr
                v-for="pack in equipmentPacksWithoutCdr"
                :key="pack.id"
              >
                <td
                  style="max-width: 200px"
                  class="text-truncate"
                >
                  <div v-if="pack.category">
                    <v-icon
                      style="font-size: 10px"
                      :color="pack.category.color"
                      class="mr-2"
                    >
                      circle
                    </v-icon>
                    {{ pack.category.name }}
                  </div>
                </td>
                <td class="font-weight-bold">
                  <a
                    href="#"
                    @click.prevent="currentPackId = pack.id; packItemsDialogVisible = true"
                  >
                    {{ pack.label }}
                  </a>
                </td>
                <td v-if="$can('view-sale-price', 'YachtEquipmentPack')">
                  {{ pack.pivot_sale_price | currency(pack.currency) }}
                </td>
                <td>
                  {{ installedByData[pack.installed_by].label }}
                </td>
                <td>
                  <span v-if="pack.supplier">
                    {{ pack.supplier.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td v-if="$can('view-purchase-price', 'YachtEquipmentPack')">
                  {{ pack.pivot_purchase_price | currency(pack.currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <currency-input
                    v-model="pack.invoiced_price"
                    dense
                    hide-details
                    outlined
                    :suffix="pack.currency"
                    style="width: 150px"
                    @change="updateInvoicedAmount(pack.id, $event)"
                  />
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <div
                    v-for="document in pack.documents"
                    :key="'document_' + pack.id + document.id"
                  >
                    <a
                      href="#"
                      @click.prevent="openDocument(document.file.id)"
                    >
                      {{ document.title }}
                    </a>
                  </div>
                  <div
                    v-for="file in equipmentFiles[pack.id]"
                    :key="file.id"
                    class="d-inline-flex"
                  >
                    <v-progress-circular
                      v-if="file.active"
                      color="grey"
                      :size="18"
                      :width="2"
                      indeterminate
                    />
                    {{ file.name }}
                  </div>
                  <div class="mt-1">
                    <file-upload
                      :ref="'upload'+pack.id"
                      :key="'upload'+pack.id"
                      v-model="equipmentFiles[pack.id]"
                      :input-id="'file'+pack.id"
                      :post-action="uploadUrl"
                      multiple
                      :data="{ type: 'invoices', yacht_id: yacht.id, yacht_equipment_pack_id: pack.id }"
                      :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                      style="cursor: pointer"
                      @input-file="inputFile(pack.id, ...arguments)"
                    >
                      <v-btn
                        color="grey darken-2"
                        class="text-none ma-0"
                        small
                        text
                      >
                        <v-icon
                          left
                          small
                        >
                          add
                        </v-icon>
                        Add Invoice
                      </v-btn>
                    </file-upload>
                  </div>
                </td>
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="text-left"
                >
                  <div v-if="pack.invoiced_price != null && pack.purchase_price != null">
                    <span :class="{ 'error--text': Number(pack.invoiced_price).toFixed() - Number(pack.purchase_price).toFixed() > 0 }">
                      {{ Math.abs(Number(pack.invoiced_price).toFixed() - Number(pack.purchase_price).toFixed()) | currency(pack.currency) }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="2">
                  <strong>Commissioning, delivery & registration</strong>
                </td>
                <td
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.cdr_price | currency(yacht.currency) }}
                </td>
                <td colspan="2" />
                <td
                  v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.cdr_purchase_price | currency(yacht.currency) }}
                </td>
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ invoicedAmounts.cdr.amount | currency(yacht.currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')" />
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <span
                    :class="{ 'error--text': invoicedAmounts.cdr.diff > 0 }"
                    class="font-weight-bold"
                  >
                    {{ Math.abs(invoicedAmounts.cdr.diff) | currency(yacht.currency) }}
                  </span>
                </td>
              </tr>
              <tr
                v-for="pack in cdrEquipmentPacks"
                :key="pack.id"
              >
                <td
                  style="max-width: 200px"
                  class="text-truncate"
                >
                  <div v-if="pack.category">
                    <v-icon
                      style="font-size: 10px"
                      :color="pack.category.color"
                      class="mr-2"
                    >
                      circle
                    </v-icon>
                    {{ pack.category.name }}
                  </div>
                </td>
                <td class="font-weight-bold">
                  <a
                    href="#"
                    @click.prevent="currentPackId = pack.id; packItemsDialogVisible = true"
                  >
                    {{ pack.label }}
                  </a>
                </td>
                <td v-if="$can('view-sale-price', 'YachtEquipmentPack')">
                  {{ pack.pivot_sale_price | currency(pack.currency) }}
                </td>
                <td>
                  {{ installedByData[pack.installed_by].label }}
                </td>
                <td>
                  <span v-if="pack.supplier">
                    {{ pack.supplier.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td v-if="$can('view-purchase-price', 'YachtEquipmentPack')">
                  {{ pack.pivot_purchase_price | currency(pack.currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <currency-input
                    v-model="pack.invoiced_price"
                    dense
                    hide-details
                    outlined
                    :suffix="pack.currency"
                    style="width: 150px"
                    @change="updateInvoicedAmount(pack.id, $event)"
                  />
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <div
                    v-for="document in pack.documents"
                    :key="'document_' + pack.id + document.id"
                  >
                    <a
                      href="#"
                      @click.prevent="openDocument(document.file.id)"
                    >
                      {{ document.title }}
                    </a>
                  </div>
                  <div
                    v-for="file in equipmentFiles[pack.id]"
                    :key="file.id"
                    class="d-inline-flex"
                  >
                    <v-progress-circular
                      v-if="file.active"
                      color="grey"
                      :size="18"
                      :width="2"
                      indeterminate
                    />
                    {{ file.name }}
                  </div>
                  <div class="mt-1">
                    <file-upload
                      :ref="'upload'+pack.id"
                      :key="'upload'+pack.id"
                      v-model="equipmentFiles[pack.id]"
                      :input-id="'file'+pack.id"
                      :post-action="uploadUrl"
                      multiple
                      :data="{ type: 'invoices', yacht_id: yacht.id, yacht_equipment_pack_id: pack.id }"
                      :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                      style="cursor: pointer"
                      @input-file="inputFile(pack.id, ...arguments)"
                    >
                      <v-btn
                        color="grey darken-2"
                        class="text-none ma-0"
                        small
                        text
                      >
                        <v-icon
                          left
                          small
                        >
                          add
                        </v-icon>
                        Add Invoice
                      </v-btn>
                    </file-upload>
                  </div>
                </td>
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="text-left"
                >
                  <div v-if="pack.invoiced_price != null && pack.purchase_price != null">
                    <span :class="{ 'error--text': Number(pack.invoiced_price).toFixed() - Number(pack.purchase_price).toFixed() > 0 }">
                      {{ Math.abs(Number(pack.invoiced_price).toFixed() - Number(pack.purchase_price).toFixed()) | currency(pack.currency) }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </div>
                </td>
              </tr>
              <tr v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                <td colspan="2">
                  <strong>Other costs</strong>
                </td>
                <td colspan="4" />
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ invoicedAmounts.other.amount | currency(yacht.currency) }}
                </td>
                <td colspan="2" />
              </tr>
              <template v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                <tr
                  v-for="(otherCost, index) in otherCosts"
                  :key="'othercost'+index"
                >
                  <template v-if="!otherCost.id || otherCost.editing">
                    <td colspan="6">
                      <v-row
                        no-gutters
                        align="center"
                      >
                        <v-col>
                          <v-text-field
                            v-model="otherCost.description"
                            outlined
                            dense
                            hide-details
                          />
                        </v-col>
                        <v-col
                          cols="auto"
                          class="pl-3"
                        >
                          <v-btn
                            color="primary"
                            :loading="otherCost.saving"
                            @click="saveOtherCost(otherCost)"
                          >
                            Save
                          </v-btn>
                        </v-col>
                        <v-col
                          v-if="otherCost.id"
                          cols="auto"
                          class="pl-3"
                        >
                          <v-btn
                            color="error"
                            text
                            @click="deleteOtherCost(otherCost.id)"
                          >
                            Delete
                          </v-btn>
                        </v-col>
                        <v-col
                          cols="auto"
                          class="pl-3"
                        >
                          <v-btn
                            color="grey"
                            small
                            text
                            @click="otherCost.id ? otherCost.editing = false : otherCosts.pop()"
                          >
                            Cancel
                          </v-btn>
                        </v-col>
                      </v-row>
                    </td>
                    <td colspan="3" />
                  </template>
                  <template v-else>
                    <td colspan="6">
                      {{ otherCost.description }}
                      <v-icon
                        class="ml-3"
                        x-small
                        @click="otherCost.editing = true"
                      >
                        fa fa-pen
                      </v-icon>
                    </td>
                    <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                      <currency-input
                        v-model="otherCost.amount"
                        dense
                        hide-details
                        outlined
                        :suffix="yacht.currency"
                        style="width: 150px"
                        @change="saveOtherCost(otherCost)"
                      />
                    </td>
                    <td>
                      <div
                        v-for="document in otherCost.documents"
                        :key="'document_other_' + otherCost.id + document.id"
                      >
                        <a
                          href="#"
                          @click.prevent="openDocument(document.file.id)"
                        >
                          {{ document.title }}
                        </a>
                      </div>
                      <div
                        v-for="file in otherCost.files"
                        :key="'othercost'+file.id"
                        class="d-inline-flex"
                      >
                        <v-progress-circular
                          v-if="file.active"
                          color="grey"
                          :size="18"
                          :width="2"
                          indeterminate
                        />
                        {{ file.name }}
                      </div>
                      <div class="mt-1">
                        <file-upload
                          :ref="'uploadothercost'+otherCost.id"
                          :key="'uploadothercost'+otherCost.id"
                          v-model="otherCost.files"
                          :input-id="'file'+otherCost.id"
                          :post-action="uploadUrl"
                          multiple
                          :data="{ type: 'invoices', yacht_id: yacht.id, yacht_other_cost_id: otherCost.id }"
                          :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                          style="cursor: pointer"
                          @input-file="inputOtherCostFile(otherCost.id, ...arguments)"
                        >
                          <v-btn
                            color="grey darken-2"
                            class="text-none ma-0"
                            small
                            text
                          >
                            <v-icon
                              left
                              small
                            >
                              add
                            </v-icon>
                            Add Invoice
                          </v-btn>
                        </file-upload>
                      </div>
                    </td>
                    <td />
                  </template>
                </tr>
              </template>
              <tr v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                <td colspan="9">
                  <v-btn
                    color="grey darken-2"
                    class="text-none ma-0"
                    small
                    text
                    @click="addOtherCost()"
                  >
                    <v-icon
                      left
                      small
                    >
                      add
                    </v-icon>
                    Add Additional Cost
                  </v-btn>
                </td>
              </tr>
              <tr class="grey lighten-4">
                <td colspan="2">
                  <strong>Total</strong>
                </td>
                <td
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.total_sale_price | currency(yacht.currency) }}
                </td>
                <td colspan="2" />
                <td
                  v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ yacht.total_purchase_price | currency(yacht.currency) }}
                </td>
                <td
                  v-if="$can('manage-invoices', 'YachtEquipmentPack')"
                  class="font-weight-bold"
                >
                  {{ invoicedAmounts.total.amount | currency(yacht.currency) }}
                </td>
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')" />
                <td v-if="$can('manage-invoices', 'YachtEquipmentPack')">
                  <span
                    :class="{ 'error--text': invoicedAmounts.total.diff > 0 }"
                    class="font-weight-bold"
                  >
                    {{ Math.abs(invoicedAmounts.total.diff) | currency(yacht.currency) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </v-card>

    <div v-if="$can('manage-invoices', 'YachtEquipmentPack') && $can('view-sale-price', 'Yacht') && $can('view-purchase-price', 'Yacht') && $can('view-finance', 'Yacht')">
      <div class="subtitle-1 pa-4">
        Yacht Sale Profit Calculation
      </div>
      <v-card
        class="pa-0 mb-16"
        elevation="10"
      >
        <div class="v-data-table theme--light">
          <div class="v-data-table__wrapper">
            <table>
              <tbody>
                <tr
                  class="grey lighten-4"
                >
                  <td style="width: 400px">
                    <strong>Yacht Sale Price</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_sale_price | currency(yacht.currency) }}
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td style="width: 400px">
                    <strong>Discount</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_sale_price_final - yacht.total_sale_price | currency(yacht.currency) }}
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td style="width: 400px">
                    <strong>Yacht Sale Price With Discount</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_sale_price_final | currency(yacht.currency) }}
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td>
                    <strong>Predicted Yacht Purchase Price</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_purchase_price | currency(yacht.currency) }}
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td>
                    <strong>Invoiced Yacht Purchase Amount</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ invoicedAmounts.total.amount | currency(yacht.currency) }} ({{ invoicedAmounts.total.amount / yacht.total_purchase_price >= 1 ? '+' : '' }}{{ (invoicedAmounts.total.amount / yacht.total_purchase_price) - 1 | percentage }})
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td>
                    <strong>Predicted Yacht Sale Profit</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_sale_price_final - yacht.total_purchase_price | currency(yacht.currency) }}
                  </td>
                </tr>
                <tr
                  class="grey lighten-4"
                >
                  <td>
                    <strong>Real Yacht Sale Profit</strong>
                  </td>
                  <td
                    class="font-weight-bold"
                  >
                    {{ yacht.total_sale_price_final - invoicedAmounts.total.amount | currency(yacht.currency) }}
                    ({{ ((yacht.total_sale_price_final - invoicedAmounts.total.amount) / (yacht.total_sale_price_final - yacht.total_purchase_price)) >= 1 ? '+' : '' }}{{ ((yacht.total_sale_price_final - invoicedAmounts.total.amount) / (yacht.total_sale_price - yacht.total_purchase_price)) - 1 | percentage }})
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-card>
    </div>

    <yacht-equipment-items-dialog
      v-if="currentPack"
      :pack="currentPack"
      :visible.sync="packItemsDialogVisible"
    />
  </div>
</template>

<script>
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import YachtEquipmentItemsDialog from '@/components/business/yachts/YachtEquipmentItemsDialog'
import CurrencyInput from '@/components/helpers/CurrencyInput.vue'
import Yacht from '@/models/Yacht'
import VueUploadComponent from 'vue-upload-component'
import File from '@/models/File'
import { cloneDeep } from 'lodash'

const otherCostModel = {
  id: null,
  description: '',
  amount: 0,
  saving: false,
  editing: false,
  files: []
}

export default {
  name: 'YachtConfiguration',
  components: { CurrencyInput, YachtEquipmentItemsDialog, FileUpload: VueUploadComponent },
  props: {
    yacht: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      installedByData: YachtEquipmentPack.installedByData,
      comments: [],
      packItemsDialogVisible: false,
      currentPackId: null,
      equipmentFiles: {},
      yachtFiles: [],
      otherCosts: []
    }
  },
  computed: {
    equipmentPacksWithoutCdr() {
      return this.yacht.equipment_packs.filter(ep => ep.category && !ep.category.is_cdr)
    },
    yardEquipment() {
      return this.equipmentPacksWithoutCdr.filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
    },
    nonYardEquipment() {
      return this.equipmentPacksWithoutCdr.filter(ep => ep.installed_by !== YachtEquipmentPack.INSTALLED_BY_SHIPYARD)
    },
    cdrEquipmentPacks() {
      return this.yacht.equipment_packs.filter(ep => ep.category && ep.category.is_cdr)
    },
    currentPack: function () {
      if (!this.currentPackId) return undefined

      return this.yacht.equipment_packs.find(p => p.id === this.currentPackId)
    },
    invoicedAmounts() {
      const equipmentAmount = Number(this.equipmentPacksWithoutCdr.map(ep => ep.invoiced_price * (ep.currency_exchange_rate || 1) || 0).reduce((a, b) => Number(a) + Number(b), 0))
      const cdrAmount = Number(this.cdrEquipmentPacks.map(ep => ep.invoiced_price * (ep.currency_exchange_rate || 1) || 0).reduce((a, b) => Number(a) + Number(b), 0))
      const yachtAmount = Number(this.yacht.base_invoiced_price)
      const otherCostsAmount = Number(this.otherCosts.map(c => c.amount || 0).reduce((a, b) => Number(a) + Number(b), 0))

      const totalAmount = (yachtAmount * (this.yacht.currency_exchange_rate || 1)) + equipmentAmount + cdrAmount + otherCostsAmount

      return {
        yacht: {
          amount: yachtAmount,
          diff: (yachtAmount - (this.yacht.base_purchase_price / (this.yacht.currency_exchange_rate || 1))).toFixed(0)
        },
        equipment: {
          amount: equipmentAmount,
          diff: (equipmentAmount - this.yacht.equipment_purchase_price).toFixed(0)
        },
        cdr: {
          amount: cdrAmount,
          diff: (cdrAmount - this.yacht.cdr_purchase_price).toFixed(0)
        },
        other: {
          amount: otherCostsAmount,
          diff: 0
        },
        total: {
          amount: totalAmount,
          diff: (totalAmount - this.yacht.total_purchase_price).toFixed(0)
        }
      }
    }
  },
  watch: {
    yacht() {
      this.loadOtherCosts()
    }
  },
  created() {
    this.uploadUrl = process.env.VUE_APP_API_URL + '/documents/upload'

    this.loadOtherCosts()

    for (let equipmentPack of this.yacht.equipment_packs) {
      this.equipmentFiles[equipmentPack.id] = []
    }
  },
  methods: {
    async updateInvoicedAmount(packId, amount) {
      try {
        await Yacht.api.updateEquipmentInvoicedPrice(this.yacht.id, packId, amount)

        this.$store.dispatch('alert', {
          'text': 'Invoiced amount updated',
          'color': 'success'
        })
      } catch (e) {
        throw e
      }
    },
    async updateYachtInvoicedAmount(amount) {
      try {
        await Yacht.api.updateYacht({
          base_invoiced_price: amount
        }, this.yacht.id)

        this.$store.dispatch('alert', {
          'text': 'Invoiced amount updated',
          'color': 'success'
        })
      } catch (e) {
        throw e
      }
    },
    inputFile(packId, newFile, oldFile) {
      if (newFile && !oldFile) {
        this.$refs['upload' + packId][0].update(newFile, { active: true })
        return
      }

      if (newFile && oldFile && !newFile.active && oldFile.active) {
        this.$refs['upload' + packId][0].remove(newFile)

        this.equipmentFiles[packId] = []

        this.$emit('update:yacht')
      }
    },
    inputYachtFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        this.$refs.uploadyacht.update(newFile, { active: true })
        return
      }

      if (newFile && oldFile && !newFile.active && oldFile.active) {
        this.$refs.uploadyacht.remove(newFile)

        this.yachtFiles = []

        this.$emit('update:yacht')
      }
    },
    inputOtherCostFile(otherCostId, newFile, oldFile) {
      if (newFile && !oldFile) {
        this.$refs['uploadothercost' + otherCostId][0].update(newFile, { active: true })
        return
      }

      if (newFile && oldFile && !newFile.active && oldFile.active) {
        this.$refs['uploadothercost' + otherCostId][0].remove(newFile)

        let otherCostIndex = this.otherCosts.findIndex(c => c.id === otherCostId)

        let otherCost = this.otherCosts[otherCostIndex]

        otherCost.files = []

        this.otherCosts.splice(otherCostIndex, 1, otherCost)

        this.$emit('update:yacht')
      }
    },
    openDocument(fileId) {
      File.api.openDocument(fileId)
    },
    addOtherCost() {
      this.otherCosts.push(cloneDeep(otherCostModel))
    },
    async saveOtherCost(otherCost) {
      if (!otherCost.description) return

      otherCost.saving = true

      try {
        let result = await Yacht.api.addOrUpdateOtherCosts(this.yacht.id, otherCost)

        this.$emit('update:yacht', result.yacht)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong saving',
          color: 'error'
        })
      } finally {
        otherCost.saving = false
      }
    },
    loadOtherCosts() {
      this.otherCosts = cloneDeep(this.yacht.other_costs).map(oc => {
        return {
          ...oc,
          editing: false,
          saving: false,
          files: []
        }
      })
    },
    async deleteOtherCost(id) {
      try {
        let result = await Yacht.api.deleteOtherCost(this.yacht.id, id)

        this.$emit('update:yacht', result.yacht)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong deleting',
          color: 'error'
        })
      }
    }
  }
}
</script>
